import type { SummaryProps } from '@javascript/types/InstantSearch';
import classNames from 'classnames';
import { ReactNode, useEffect, useRef } from 'react';

export interface InstantSearchAccordionProps extends SummaryProps {
  children: ReactNode;
  additionalStyling?: string;
  disabled?: boolean;
  selectedOptions?: string;
}

const InstantSearchAccordion = ({
  additionalStyling,
  children,
  disabled,
  label,
  labelSuffix,
  selectedOptions,
  filtersOpen,
}: InstantSearchAccordionProps) => {
  const ref = useRef<HTMLDetailsElement>(null);

  // automatically close the accordion if disabled
  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (disabled || filtersOpen) {
      node.removeAttribute('open');
    }
  }, [disabled, filtersOpen]);

  // scroll to the summary button when accordion closes
  const onSummaryToggle = () => {
    requestAnimationFrame(() => {
      if (!ref.current) return;
      if (!ref.current.open) {
        ref.current.scrollIntoView({ block: 'nearest' });
      }
    });
  };

  return (
    <div
      className={classNames('ch-accordion', additionalStyling, {
        'ch-accordion--disabled': disabled,
      })}
    >
      <details className="ch-bb--1 ch-bc--grey-3" ref={ref}>
        <summary onClick={onSummaryToggle}>
          <div className="ch-width--11">
            {label}
            {labelSuffix && (
              <span className="ch-fw--400">{' ' + labelSuffix}</span>
            )}
            {selectedOptions && (
              <span className="ac-instant-search__selected-options ch-fs--2 ch-fw--500 ch-display--block ch-text--ellipsis">
                {selectedOptions}
              </span>
            )}
          </div>
        </summary>
        <div className="ch-accordion__content sl:ch-ph--6 sl:ch-pv--5 ch-pb--0 ch-pt--3">
          {children}
        </div>
      </details>
    </div>
  );
};

export default InstantSearchAccordion;
